import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';

const categoryNames: Record<string, string> = {
  movie: '电影',
  tv: '电视剧',
  variety: '综艺',
  anime: '动漫',
};

export default function Article() {
  const { articleId } = useParams<{ articleId: string }>();
  const movie = getMovieById(Number(articleId));

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-950 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-error-warning-line text-5xl text-foreground-500"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-100 mb-2">影片不存在</h1>
          <p className="text-foreground-400 text-sm mb-6">抱歉，您要查看的影片可能已下架或不存在。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedMovies = getRelatedMovies(movie, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    dateCreated: movie.releaseDate,
    director: { '@type': 'Person', name: movie.director },
    actor: movie.actors.map((a) => ({ '@type': 'Person', name: a })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
      ratingCount: movie.views,
    },
    countryOfOrigin: { '@type': 'Country', name: movie.region },
    url: `https://chengquan.tv/article/${movie.id}`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://chengquan.tv/' },
      { '@type': 'ListItem', position: 2, name: categoryNames[movie.category] || '分类', item: `https://chengquan.tv/category/${movie.category}` },
      { '@type': 'ListItem', position: 3, name: movie.title },
    ],
  };

  return (
    <>
      <SEO
        title={movie.title}
        description={movie.description}
        keywords={`${movie.title},${movie.subcategory},${movie.tags.join(',')},成全影院`}
        canonicalUrl={`https://chengquan.tv/article/${movie.id}`}
        ogType="video.movie"
        schema={articleSchema}
      />

      <div className="min-h-screen bg-background-950">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryNames[movie.category] || '分类', path: `/category/${movie.category}` },
                { label: movie.title },
              ]}
            />

            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
            />

            {/* Movie Detail */}
            <article className="mt-6">
              <div className="flex flex-col lg:flex-row gap-8">
                {/* Poster */}
                <div className="flex-shrink-0 w-full sm:w-[280px] lg:w-[320px]">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-800">
                    <img
                      src={movie.poster}
                      alt={`${movie.title} - ${movie.subcategory}电影海报`}
                      title={`${movie.title} - 成全影院高清在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    {movie.isHot && (
                      <span className="bg-primary-500 text-background-50 text-xs font-bold px-2.5 py-0.5 rounded-md whitespace-nowrap">
                        热播
                      </span>
                    )}
                    {movie.isNew && (
                      <span className="bg-accent-500 text-background-950 text-xs font-bold px-2.5 py-0.5 rounded-md whitespace-nowrap">
                        NEW
                      </span>
                    )}
                    {movie.tags.map((tag) => (
                      <span key={tag} className="text-xs text-foreground-400 bg-background-800 px-2 py-0.5 rounded-md">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-3">
                    {movie.title}
                  </h1>
                  <p className="text-sm text-foreground-400 mb-1">{movie.englishTitle}</p>

                  <div className="flex items-center gap-4 mb-5">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-400 text-lg"></i>
                      <span className="text-accent-400 font-bold text-xl">{movie.rating}</span>
                      <span className="text-foreground-500 text-xs">/ 10</span>
                    </div>
                    <span className="text-foreground-500 text-sm">{movie.views.toLocaleString()} 次观看</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 mb-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-foreground-500 flex-shrink-0 min-w-[60px]">导演：</span>
                      <span className="text-sm text-foreground-200">{movie.director}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-foreground-500 flex-shrink-0 min-w-[60px]">主演：</span>
                      <span className="text-sm text-foreground-200">{movie.actors.join(' / ')}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-foreground-500 flex-shrink-0 min-w-[60px]">类型：</span>
                      <span className="text-sm text-foreground-200">{movie.subcategory}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-foreground-500 flex-shrink-0 min-w-[60px]">地区：</span>
                      <span className="text-sm text-foreground-200">{movie.region}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-foreground-500 flex-shrink-0 min-w-[60px]">时长：</span>
                      <span className="text-sm text-foreground-200">{movie.duration}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-foreground-500 flex-shrink-0 min-w-[60px]">集数：</span>
                      <span className="text-sm text-foreground-200">{movie.episodes}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-foreground-500 flex-shrink-0 min-w-[60px]">年份：</span>
                      <span className="text-sm text-foreground-200">{movie.year}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-foreground-500 flex-shrink-0 min-w-[60px]">上映：</span>
                      <span className="text-sm text-foreground-200">{movie.releaseDate}</span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h2 className="text-base font-semibold text-foreground-100 mb-2">剧情简介</h2>
                    <p className="text-sm text-foreground-300 leading-relaxed">{movie.description}</p>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap">
                      <i className="ri-play-circle-line text-lg"></i>
                      立即播放
                    </button>
                    <button className="inline-flex items-center gap-2 bg-background-800 hover:bg-background-700 text-foreground-200 px-5 py-3 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap">
                      <i className="ri-heart-line"></i>
                      收藏
                    </button>
                    <button className="inline-flex items-center gap-2 bg-background-800 hover:bg-background-700 text-foreground-200 px-5 py-3 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap">
                      <i className="ri-share-forward-line"></i>
                      分享
                    </button>
                  </div>
                </div>
              </div>
            </article>

            {/* Related Movies */}
            {relatedMovies.length > 0 && (
              <section className="mt-14" aria-label="相关推荐">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-50">相关推荐</h3>
                  <Link
                    to={`/category/${movie.category}`}
                    className="text-sm text-foreground-400 hover:text-primary-400 transition-colors flex items-center gap-1"
                  >
                    更多 <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} size="medium" />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}